<script lang="ts">
	import { cn } from "#lib/web-ui/ui/utils.js";
	import type { WithElementRef } from "bits-ui";
	import type { HTMLAttributes } from "svelte/elements";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import DollarSignIcon from "@lucide/svelte/icons/dollar-sign";
	import SparklesIcon from "@lucide/svelte/icons/sparkles";
	import ExternalLinkIcon from "@lucide/svelte/icons/external-link";
	import { getRentalTemplateContext } from "./rental-template-context.svelte";

	const templateContext = getRentalTemplateContext()!;
</script>

{#if templateContext.floatingBanner}
	<!-- Desktop banner - only visible on md screens and up -->
	<div class={"relative z-10 mx-auto -mb-16 hidden max-w-5xl md:block"}>
		<div class="-mt-16"></div>

		<!-- Animated glow effect -->
		<div
			class="from-primary to-primary absolute -inset-1 animate-pulse rounded bg-gradient-to-r via-yellow-400 opacity-30 blur-sm"
		></div>

		<div class="border-primary relative overflow-hidden rounded border-2 shadow-2xl">
			<!-- Animated background gradient -->
			<div
				class="animate-gradient-x absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900"
			></div>

			<!-- Sparkle effects -->
			<div class="absolute top-2 right-4 animate-bounce">
				<SparklesIcon class="text-primary h-4 w-4 opacity-70" />
			</div>
			<div class="absolute bottom-2 left-6 animate-bounce delay-1000">
				<SparklesIcon class="h-3 w-3 text-yellow-400 opacity-50" />
			</div>

			<div class="relative px-10 py-8">
				<div class="flex items-center justify-between gap-6">
					<div class="flex items-center gap-4">
						<!-- Enhanced pulsing icon -->
						<div
							class="from-primary shrink-0 animate-pulse rounded-full bg-gradient-to-br to-yellow-500 p-3 shadow-lg"
						>
							<DollarSignIcon class="h-6 w-6 text-white drop-shadow-sm" />
						</div>
						<div class="text-left">
							<!-- Enhanced typography with glow -->
							<h3 class="text-xl font-bold text-white drop-shadow-sm">
								<span
									class="bg-gradient-to-r from-white to-yellow-100 bg-clip-text text-transparent"
								>
									{templateContext.floatingBanner?.title}
								</span>
							</h3>
							<p class="mt-1 font-medium text-neutral-200">
								{templateContext.floatingBanner?.subTitle}
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- Mobile floating banner -->
	<div
		class={cn(
			"fixed right-0 bottom-0 left-0 z-50 mx-auto mb-4 max-w-2xl translate-y-0 opacity-100 transition-all duration-500 ease-out md:hidden"
		)}
	>
		<!-- Mobile glow effect -->
		<div
			class="from-primary to-primary absolute -inset-1 animate-pulse rounded bg-gradient-to-r via-yellow-400 opacity-40 blur-sm"
		></div>

		<div class="border-primary relative mx-4 overflow-hidden rounded border-2 shadow-2xl">
			<!-- Animated background -->
			<div
				class="animate-gradient-x absolute inset-0 bg-gradient-to-r from-neutral-900 via-neutral-800 to-neutral-900"
			></div>

			<!-- Sparkle effects for mobile -->
			<div class="absolute top-1 left-12 animate-bounce">
				<SparklesIcon class="text-primary h-2 w-2 opacity-70" />
			</div>

			<div class="relative px-4 py-3">
				<div class="flex items-center justify-between">
					<div class="flex items-center gap-3">
						<!-- Enhanced mobile icon -->
						<div
							class="from-primary shrink-0 animate-pulse rounded-full bg-gradient-to-br to-yellow-500 p-2"
						>
							<DollarSignIcon class="h-4 w-4 text-white" />
						</div>
						<div>
							<span
								class="bg-gradient-to-r from-white to-yellow-100 bg-clip-text text-sm font-bold text-white"
							>
								{templateContext.floatingBanner?.title}
							</span>
							<span class="ml-2 text-xs font-medium text-neutral-200">
								{templateContext.floatingBanner?.subTitle}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	@keyframes gradient-x {
		0%,
		100% {
			background-size: 200% 200%;
			background-position: left center;
		}
		50% {
			background-size: 200% 200%;
			background-position: right center;
		}
	}

	.animate-gradient-x {
		animation: gradient-x 3s ease infinite;
	}
</style>
